import './index.scss';
import React, { useState, useReducer, useEffect, useMemo, useRef, useCallback } from 'react';
import moment from 'moment';
import { debounce } from 'lodash-es';
import { useIntl, history, getLocale } from 'umi';
import { Table } from 'tea-component';
import Filters from './components/Filters';
import StatsLineChart from './components/LineChart';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import DynamicTable from '../../components/DynamicColTable/Table';
import GenreProfileSearch from './components/Search';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import {
  MARKET_DIMENSION_CONFIG,
  METRIC_KEY_MAP,
  PAGE_LOG_CONFIG_KEYS,
  GRANULARITY_MAP,
} from './config';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
// import { getDefulatTimeValueByDateType, toDateTypeFilter } from '@/pages/intelligence/utils/common';
import useSetQueryDefinition, {
  COND_PARAMS,
  FULL_GENRE,
} from '@intelligence/hooks/useQueryDefinition';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import {
  getMobilePageData,
  getMobileStatsGameChart,
  getMobileStatsGameTable,
  getMobileStatsCompanyChart,
  getMobileStatsCompanyTable,
} from '@intelligence/services/marketProfile';
import { getGenresReqParams } from '@intelligence/components/GenreSelectMiddleware/Supports/utils';
import { useGenreMap } from '@/pages/intelligence/pages/MarketProfile/hooks/useGenreMap';
import { IStatsMobileProps, IStatsFilter, IAction, TDimension, TEntityType, IModifiedParams, TTableItem } from './type';

const { pageable } = Table.addons;

const reducer = (state: IStatsFilter, { type, payload }: IAction) => ({
  ...state,
  [type]: payload,
});

const MAX_CHECKED = 10;

export default function Stats(props: IStatsMobileProps) {
  const {
    entityType,
    marketNameEn,
    genreSource,
    filter,
    filterDispatch,
    leftSegment = '',
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [tableData, setTableData] = useState<TTableItem[]>([]);
  // 用于搜索后重置
  const originalTableData = useRef([]);
  const [lineChartData, setLineChartData] = useState([]);
  const originalLineData = useRef([]);
  const [lineChartSumData, setLineChartSumData] = useState([]);
  // 页面打点
  const [pageLogInfo, setPageLogInfo] = useState<any>({});
  const [ieggGenreReq, setIeggGenreReq] = useState<string[]>([]);
  const [metricsList, setMetricsList] = useState([]);
  // 缓存最后一次组装接口请求入参的结果，用于搜索（由于搜索组件内 有useEffect导致 获取不到最新的参数，需要用这个ref来实现获取到最新的参数）
  const cachedParams = useRef<any>({});
  // 在组件顶部添加AbortController引用
  const abortControllerRef = useRef<AbortController | null>(null);

  const { annieGenreMap, ieggGenreMap, isLoading: isGenreLoading } = useGenreMap();
  const currentGenreMap = genreSource === 'appannie' ? annieGenreMap : ieggGenreMap;

  // 获取当前页面的配置文件
  const getCurPageConfig = ({ marketType, dimension }: {
    marketType?: TEntityType;
    dimension?: TDimension;
  }) => {
    const marketPage = MARKET_DIMENSION_CONFIG[marketType || filter.marketType || 'mobile'];
    const dimen = dimension || filter.dimension || 'games';
    return {
      title: marketPage.title?.[dimen],
      lineMetricList: marketPage.lineMetricList?.[dimen],
      lineTotalText: marketPage.lineTotalText?.[dimen],
      lineMetricInfo: null,
      tableMetricList: marketPage.tableMetricList?.[dimen],
      tableFirstCol: marketPage.firstCol?.[dimen],
      tableSpecialCol: marketPage.specialCol?.[dimen],
      tableFormatter: marketPage.tableFormatter?.[dimen],
      lineFormatter: marketPage.lineFormatter?.[dimen],
      keyMap: marketPage.keyMap?.[dimen],
      ratioHideCol: marketPage.ratioHideCol?.[dimen],
    };
  };

  // 当前页面配置文件的缓存，有异步问题
  const curPageConfig = useMemo(() => getCurPageConfig({}), [filter.marketType, filter.dimension]);

  const tableScrollWidth = useMemo(
    () => {
      if (filter.dimension !== 'games') {
        return 1100;
      }
      if (filter.marketType === 'mobile') {
        return 2200;
      }
      return 1850;
    },
    [filter.marketType, filter.dimension],
  );

  const [tableAction, tableActionDispatch] = useReducer(reducer, {
    total: 0,
    pageIndex: 1,
    pageSize: 50,
    sortKey: '',
    sortKeyLabel: '',
    sortOrder: 'desc',
    selectedList: [],
    SelectionAll: [],
    isRatioChange: false,
  });

  const [loading, loadingDispatch] = useReducer(reducer, {
    table: false,
    lineChart: false,
  });

  // 搜索列表点击某条数据, byGame: 填入id; byCountry: 填入name_abbr; byCompany: 填入name
  const getCheckedCode = (list: TTableItem) => {
    const keyMap = curPageConfig.keyMap() || {};
    return list?.map((item) => item[keyMap.id || 'id']);
  };

  // 日期组件返回的endDate是月最后一天，组件判断变化的时候会出现错误，所以要先转成月第一天
  const getFirstDayOfMonth = (date: string): string => {
    const m = date ? moment(date) : moment();
    return m?.startOf('month')?.format('YYYY-MM-DD');
  };

  // 获取参数，modifiedParams是异步时传过来的参数
  const getParams = ({ type, modifiedParams = {} }: { type: 'table' | 'line'; modifiedParams: IModifiedParams; }) => {
    const tmpCurPageConfig = getCurPageConfig({
      marketType: modifiedParams?.entity_type,
      dimension: modifiedParams?.data_tag,
    });

    const granularity = modifiedParams?.granularity || filter.granularity || 'monthly';
    const dimension = modifiedParams?.data_tag || filter.dimension || 'operation';
    setIeggGenreReq(modifiedParams?.iegg_genre_req || []);
    const getGamesOrCompaniesParams = (type: 'table' | 'line') => {
      // 根据粒度过滤au字段
      const params = {
        entity_type: entityType,
        granularity,
        start_date: modifiedParams?.start_date || filter.startDate,
        end_date: modifiedParams?.end_date || filter.endDate,
        market: marketNameEn,
        sources: modifiedParams?.sources || [filter.dataSource],
        iegg_genre_req: modifiedParams?.iegg_genre_req || [],
      };
      const lineParams = {
        ...params,
        select_code: modifiedParams?.checkedList
          ? getCheckedCode(modifiedParams?.checkedList)
          : getCheckedCode(tableAction.selectedList),
      };
      const tableParams = {
        ...params,
        order: modifiedParams?.order || tableAction.sortOrder,
        order_metric: modifiedParams?.order_metric || tableAction.sortKey.split('_')?.[1],
        order_source: modifiedParams?.order_source || filter.dataSource,
        page: modifiedParams?.page || tableAction.pageIndex,
        page_size: modifiedParams?.page_size || tableAction.pageSize,
        keyword: '',
        b_ratio: Object.prototype.hasOwnProperty.call(modifiedParams, 'isRatioChange')
          ? !!modifiedParams?.isRatioChange
          : !!tableAction.isRatioChange,
      };
      return type === 'table' ? tableParams : lineParams;
    };

    const getOperationParams = () => ({
      genres: modifiedParams?.genres || filter.genres,
      // 后端要求platforms传All，否则数据会翻倍
      platforms: ['All'],
      metrics: ['revenue', 'download'],
      source_metrics: modifiedParams?.source_metrics || {
        [filter.dataSource]: ['revenue', 'download'],
      },
      granularity,
      start_date: modifiedParams?.start_date || filter.startDate,
      end_date: modifiedParams?.end_date || filter.endDate,
      region_type: 'market',
      regions: [marketNameEn],
      b_group_by_date: true,
      b_growth_ratio: true,
      b_group_by_market: true,
    });

    /**
     * 将品类数组中的 'All' 转换为空数组，因为查询All时会只返回All数据，表格数据会过滤all
     * @param genres 品类字符串数组
     * @returns 如果数组内容为 'All' 则返回空数组,否则返回原数组
     */
    const changeGenreAllToEmpty = (genres: string[] = []) =>
      genres?.toString() === 'All' ? [] : genres;

    const getGenresParams = (type: 'table' | 'line') => ({
      genres:
        type === 'line'
          ? modifiedParams?.checkedList?.map((item: any) => item.genre)
          : modifiedParams?.genres || changeGenreAllToEmpty(filter.genres),
      // 后端要求platforms传All，否则数据会翻倍
      platforms: ['All'],
      metrics: ['revenue', 'download'],
      source_metrics: modifiedParams?.source_metrics || {
        [filter.dataSource]: ['revenue', 'download'],
      },
      granularity,
      start_date: modifiedParams?.start_date || filter.startDate,
      end_date: modifiedParams?.end_date || filter.endDate,
      region_type: 'market',
      regions: [marketNameEn],
      b_group_by_genre: true,
      b_growth_ratio: type === 'table',
      b_group_by_date: type === 'line',
    });

    if (modifiedParams?.order_metric) {
      Object.assign(modifiedParams, {
        order_metric: modifiedParams?.order_metric.split('_')?.[1],
      });
    }

    // genres要特殊判断，把All转为空数组，不然最终还会把modifiedParams合入参数去请求
    if (dimension === 'genres') {
      Object.assign(modifiedParams, {
        genres: changeGenreAllToEmpty(modifiedParams?.genres || []),
      });
    }

    const PARAMS_MAP: any = {
      games: getGamesOrCompaniesParams,
      companies: getGamesOrCompaniesParams,
      operation: getOperationParams,
      genres: getGenresParams,
    };

    setMetricsList(tmpCurPageConfig.tableMetricList?.map((item: any) => item.metric) || []);
    const finalParams = Object.assign({}, modifiedParams, PARAMS_MAP[dimension](type));
    // 表格的请求参数中，select_code是不用的，会影响请求结果
    if (type === 'table') {
      // 每次重新组装接口入参后，更新缓存的接口入参，用于搜索时获取页面参数用
      cachedParams.current = finalParams;
    }
    if (Object.prototype.hasOwnProperty.call(finalParams, 'checkedList')) {
      delete finalParams.checkedList;
    }
    return finalParams;
  };

  // 前端搜索的过滤数据
  const handleSearchListFront = (searchKey: string) =>
    originalTableData.current?.filter((item: any) =>
      item[curPageConfig.keyMap()?.gametitle].toLowerCase()?.includes(searchKey.toLowerCase()),
    );

  // 搜索时，获取页面的参数
  const getParamsForSearch = (inputValue: string, type: string) => {
    const add =
      type === 'search'
        ? {
          keyword: inputValue,
        }
        : {
          select_code: [inputValue],
        };
    const params = Object.assign({}, cachedParams.current, add);
    return params;
  };

  /**
   * 对表格数据进行排序处理的函数
   * @param {Object} params - 排序参数对象
   * @param {any} params.list - 需要排序的数据列表
   * @param {string} [params.sortKey] - 排序的键名
   * @param {string} [params.sortOrder] - 排序顺序，'desc' 或 'asc'
   * @param {('main'|'other')} params.sortPosition - 排序位置，'main' 表示从第一层数据中查找，'other' 表示从 metricList 中查找
   * @param {string} params.matchKey - 匹配的键名
   * @param {('date'|'other')} params.valueType - 值类型，'date' 表示日期类型，'other' 表示其他类型
   * @param {any} params.pageConfig - 页面配置对象，包含键名映射等信息
   * @returns {Array} 排序后的数据列表
   *
   * @description
   * 该函数主要用于处理表格数据的排序，支持普通值排序和环比值排序。
   * 当进行环比值排序时，会将环比为空的数据放在排序结果的末尾。
   * 支持日期类型的特殊处理。
   */
  const getSortedTableData = ({
    list,
    sortKey,
    sortOrder,
    sortPosition,
    matchKey,
    valueType,
    pageConfig,
  }: {
    list: any;
    sortKey?: string;
    sortOrder?: string;
    sortPosition: 'main' | 'other';
    matchKey: string;
    valueType: 'date' | 'other';
    pageConfig: any;
  }) => {
    const trueSortKey = !sortKey ? tableAction.sortKey : sortKey;
    const trueSortOrder = !sortOrder ? tableAction.sortOrder : sortOrder;
    if (!trueSortKey) return;
    const len = trueSortKey?.length;
    // 获取类型，是值还是环比值
    const type = trueSortKey?.slice(len - 1);
    const targetSortKey = trueSortKey?.slice(0, len - 2);

    /**
     * 获取目标排序值,从传入的list中获取对比的值，当sortPosition为main的时候，从list的第一层中找，否则从metricList中找
     * @param sortItem - 排序项数据对象
     * @returns 返回排序值
     * @description 根据排序位置和类型获取对应的排序值:
     * - 如果排序位置是main,直接返回matchKey对应的值
     * - 否则从metricList中查找对应metric的值
     * - 支持从metricValue或metricRatio中获取值
     */
    const getTargetValue = (sortItem: any) => {
      const keyMap = pageConfig?.keyMap();
      const metricListKey = keyMap?.metricList || 'metrics';
      const metricKey = keyMap?.metricName || 'metrics';
      // 根据类型判断从metricValue还是metricRatio中取值
      const typeStr = type === '0' ? keyMap?.metricValue : keyMap?.metricRatio;
      return sortPosition === 'main'
        ? sortItem[matchKey]
        : sortItem[metricListKey]?.find((item: any) => item[metricKey] === targetSortKey)?.[
        typeStr
        ];
    };
    /**
     * 过滤并分类指标数据，主要作用是将环比（dod）为空的和不为空的拆分不同数组
     * @description 将指标列表按照环比数据是否为空进行分类
     * @returns {Object} 返回分类后的数组对象
     *   - isDodNullArr: 环比为空的指标数组
     *   - isDodNotNullArr: 环比不为空的指标数组
     */
    const filter = () => {
      const keyMap = pageConfig?.keyMap();
      const metricListKey = keyMap?.metricList || 'metrics';
      const metricKey = keyMap?.metricName || 'metrics';
      const metricRatio = keyMap?.metricRatio || 'dod';
      const isDodNullArr: any[] = [];
      const isDodNotNullArr: any[] = [];
      list?.[metricListKey]?.forEach((item: any) => {
        if (item?.[metricKey]?.[metricRatio] === null) {
          isDodNullArr.push(item);
        } else {
          isDodNotNullArr.push(item);
        }
      });
      return {
        isDodNullArr,
        isDodNotNullArr,
      };
    };
    /**
     * 比较函数,用于数组排序
     * @param a 比较对象 a
     * @param b 比较对象 b
     * @returns 比较结果
     * - 如果 valueType 为 'date',将值转换为 moment 对象进行比较
     * - 根据 sortOrder 决定升序或降序:
     *   - desc: 降序排列(b-a)
     *   - asc: 升序排列(a-b)
     */
    const compareFun = (a: any, b: any) => {
      let aValue = getTargetValue(a);
      let bValue = getTargetValue(b);
      if (valueType === 'date') {
        aValue = moment(aValue);
        bValue = moment(bValue);
      } else {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);
      }
      // 没有值的时候，降序默认放到最后
      if (!aValue || !bValue) {
        return trueSortOrder === 'desc' ? 1 : 0;
      }
      return trueSortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    };
    // 如果是以值排序或者没有环比数据时，直接使用sort方法
    if (type === '0' || type === '1') {
      return list.sort(compareFun);
    }
    // 如果是以环比排序，需要拼接数组
    const { isDodNullArr, isDodNotNullArr } = filter();
    return isDodNotNullArr.sort(compareFun).concat(...isDodNullArr);
  };

  const handleDateFilterWhenCompaniesAndSensorTower = ({ modifiedParams }: { modifiedParams: IModifiedParams; }) => {
    const startDate = getFirstDayOfMonth(filter.startDate);
    const endDate = getFirstDayOfMonth(filter.endDate);
    filterDispatch({ type: 'granularity', payload: 'monthly' });
    filterDispatch({ type: 'startDate', payload: startDate });
    filterDispatch({ type: 'endDate', payload: endDate });
    Object.assign(modifiedParams, {
      [METRIC_KEY_MAP.rangeTime.granularity]: 'monthly',
      // 切换粒度的时候，需要清空折线图的选中
      [METRIC_KEY_MAP.selectedList]: [],
      start_date: startDate,
      end_date: endDate,
    });
  };

  // 筛选器改变时候的回调
  const handleFilterChange = ({ type, payload }: any) => {
    let modifiedParams = {};
    // 在切换市场类型或者维度后，要重置选中的code
    if (['marketType', 'dimension'].includes(type)) {
      modifiedParams = {
        select_code: [],
      };
    }
    if (type === 'cascade') {
      const { granularity } = payload;
      let startDate = payload.start_date || filter.startDate;
      let endDate = payload.end_date || filter.endDate;
      // 公司没有st数据源的月粒度数据，所以强制切换粒度后，必须把开始和结束时间统一设置为月粒度，这样筛选器才能选中月粒度
      if (!payload.start_date || filter.startDate !== startDate) {
        startDate = granularity === 'monthly' ? getFirstDayOfMonth(startDate) : startDate;
        filterDispatch({ type: 'startDate', payload: startDate });
        Object.assign(modifiedParams, {
          [METRIC_KEY_MAP.rangeTime.startDate]: startDate,
        });
      }
      if (!payload.end_date || filter.endDate !== endDate) {
        endDate = granularity === 'monthly' ? getFirstDayOfMonth(endDate) : endDate;
        filterDispatch({ type: 'endDate', payload: endDate });
        Object.assign(modifiedParams, {
          [METRIC_KEY_MAP.rangeTime.endDate]: endDate,
        });
      }
      if (filter.granularity !== granularity) {
        filterDispatch({ type: 'granularity', payload: granularity });
        Object.assign(modifiedParams, {
          [METRIC_KEY_MAP.rangeTime.granularity]: granularity,
          // 切换粒度的时候，需要清空折线图的选中
          [METRIC_KEY_MAP.selectedList]: [],
          start_date: getFirstDayOfMonth(filter.startDate),
          end_date: getFirstDayOfMonth(filter.endDate),
        });
      }
    } else if (type === 'dataSource') {
      const sourceMetrics = {
        [payload]: ['revenue', 'download'],
      };
      filterDispatch({ type: 'dataSource', payload });
      // 公司的SensorTower没有日和周粒度数据，所以要强制切换为月粒度
      if (filter.dimension === 'companies' && payload === 'sensortower' && filter.granularity !== 'monthly') {
        handleDateFilterWhenCompaniesAndSensorTower({ modifiedParams });
      }
      if (filter.dimension === 'games' || filter.dimension === 'companies') {
        Object.assign(modifiedParams, {
          sources: [payload],
          order_source: payload,
        });
      } else {
        Object.assign(modifiedParams, {
          sources: payload,
          source_metrics: sourceMetrics,
        });
      }
    } else if (type === 'mutipleGenre') {
      const genreData = getGenresReqParams({}, payload);
      filterDispatch({ type: 'mainGenres', payload: payload?.mainGenres });
      filterDispatch({ type: 'subGenres', payload: payload?.subGenres });
      setIeggGenreReq(genreData.iegg_genre_req || []);
      Object.assign(modifiedParams, {
        iegg_genre_req: genreData.iegg_genre_req,
      });
    } else {
      const targetKey = METRIC_KEY_MAP[type];
      filterDispatch({ type, payload });
      Object.assign(modifiedParams, {
        [targetKey]: payload,
      });
    }
    tableActionDispatch({
      type: 'pageIndex',
      payload: 1,
    });
    initial({
      modifiedParams: {
        ...modifiedParams,
        page: 1,
      },
      init: true,
    });
  };

  const handlePagingChange = (e: any) => {
    if (filter.dimension === 'operation') return pageable;
    const { pageIndex, pageSize } = e;
    tableActionDispatch({
      type: 'pageIndex',
      payload: pageIndex,
    });
    tableActionDispatch({
      type: 'pageSize',
      payload: pageSize,
    });
    getTableData({
      modifiedParams: {
        order: tableAction.sortOrder,
        order_metric: tableAction.sortKey,
        page: pageIndex,
        page_size: pageSize,
      },
      isFrontRender: filter.dimension === 'genres',
    });
    // initial({
    //   modifiedParams: {
    //     page: pageIndex,
    //     page_size: pageSize,
    //   }
    // })
  };

  const handleSortChange = (e: any) => {
    const { key, sortVal, ratioType } = e;
    const sortKey = key.slice(0, key.length - 2);
    const isRatio = key.slice(key.length - 1) === '1';
    const trueSortKey = isRatio ? `${sortKey}_ratio` : sortKey;
    tableActionDispatch({
      type: 'sortKey',
      payload: trueSortKey,
    });
    tableActionDispatch({
      type: 'sortKeyLabel',
      payload: key,
    });
    tableActionDispatch({
      type: 'sortOrder',
      payload: sortVal,
    });
    tableActionDispatch({
      type: 'pageIndex',
      payload: 1,
    });
    tableActionDispatch({
      type: 'isRatioChange',
      payload: ratioType === 1,
    });
    initial({
      init: true,
      isFrontSort: ['operation', 'genres'].includes(filter.dimension),
      isRequestLineData: filter.dimension === 'genres',
      modifiedParams: {
        order: sortVal,
        order_metric: trueSortKey,
        originalSortKey: key,
        page: 1,
        isRatioChange: ratioType === 1,
      },
    });
  };

  const getIsHideCol = (condStr: string) => false;

  const handleDateColFormat = (title: string) => {
    switch (filter.granularity) {
      case 'monthly':
        return moment(title).format('YYYY-MM');
      // return moment(title).format('MMM, YYYY');
      default:
        return moment(title).format('YYYY-MM-DD');
      // return moment(title).format('MMM DD, YYYY');
    }
  };

  const handleGameItemClick = (type: string, item: any) => {
    const keyMap = curPageConfig.keyMap();
    const linkUrl = MARKET_DIMENSION_CONFIG.common.getLinkUrl(filter.dimension, filter.marketType, {
      id: item[keyMap.id],
      nameCn: item[keyMap.gametitleCn],
    });
    linkUrl && history.push(linkUrl);
  };

  // checkbox的回调
  const handleTableCheckboxChange = useCallback(debounce((list: any, status: string) => {
    // console.log(list, status);
    const isMoreThanTen = (_list: TTableItem) => _list.length >= MAX_CHECKED;
    let checked = list;
    switch (status) {
      case 'uncheck all':
        tableActionDispatch({ type: 'selectedList', payload: list });
        // 取消全选时直接置空，不调接口
        setLineChartData([]);
        return;
      case 'check all':
        if (tableAction.selectedList > MAX_CHECKED) {
          const topTen = list.slice(0, MAX_CHECKED);
          checked = topTen;
          tableActionDispatch({ type: 'selectedList', payload: topTen });
        } else {
          tableActionDispatch({ type: 'selectedList', payload: list });
        }
        break;
      case 'uncheck': {
        const removedArr = tableAction.selectedList?.filter((item: TTableItem) => item?.name !== list.name);
        checked = removedArr;
        tableActionDispatch({ type: 'selectedList', payload: removedArr });
        break;
      }
      case 'check': {
        if (isMoreThanTen(tableAction.selectedList)) return;
        const newArrt = tableAction.selectedList?.concat(list);
        checked = newArrt;
        tableActionDispatch({ type: 'selectedList', payload: newArrt });
        break;
      }
      default:
        break;
    }
    getLineChartData({
      modifiedParams: {
        checkedList: checked,
      },
    });
  }, 500), [filter.dimension, tableAction.selectedList]);

  const setTableCheck = (list: TTableItem[]) => {
    tableActionDispatch({ type: 'SelectionAll', payload: list });
  };

  const setDefaultDate = ({ startDate, endDate }: { startDate: string; endDate: string; }) => {
    if (!filter.startDate && !filter.endDate) {
      filterDispatch({ type: 'startDate', payload: getFirstDayOfMonth(startDate) });
      filterDispatch({ type: 'endDate', payload: getFirstDayOfMonth(endDate) });
    }
  };

  // const getDefaultDateValueByType = (dateType: string) => {
  //   if (toDateTypeFilter[dateType] === 'month') {
  //     return [
  //       getFirstDayOfMonth(moment().subtract(12, 'month').endOf('month').format('YYYY-MM-DD')),
  //       getFirstDayOfMonth(moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')),
  //     ];
  //   }
  //   return getDefulatTimeValueByDateType(dateType);
  // };

  /**
   * 渲染表格数据的处理函数
   * @param {Object} params - 表格数据参数对象
   * @param {Array} params.tableList - 表格数据列表
   * @param {Array} params.checkList - 选中项列表
   * @param {Object} params.cond - 条件对象，包含开始日期、结束日期和总数
   * @param {boolean} [isOuter] - 是否为外部数据，决定是否需要格式化
   * @param {boolean} [isNoNeedFormat] - 是否不需要格式化
   *
   * 该函数负责处理表格数据的渲染逻辑，包括：
   * - 设置默认日期范围
   * - 处理数据总数
   * - 根据条件决定是否格式化数据
   * - 更新表格数据状态
   * - 更新加载状态
   */
  const renderTableData = (
    { tableList, checkList, cond }: { tableList: TTableItem[]; checkList: TTableItem[]; cond: any; },
    isOuter?: boolean,
    isNoNeedFormat?: boolean,
  ) => {
    let tmpTableList: TTableItem[] = tableList;
    let tmpCheckList: TTableItem[] = checkList;
    if (!tableList && !originalTableData.current) return;
    if (!tableList) tmpTableList = originalTableData.current;
    const { startDate, endDate } = cond || {};
    let { total } = cond || {};
    if ((!filter.startDate || !filter.endDate) && startDate && endDate) {
      setDefaultDate({ startDate, endDate });
    }
    if (!total) {
      total = tmpTableList.length;
    }
    if (!checkList) {
      tmpCheckList = tmpTableList;
    }
    if (isOuter) {
      const { result } = !isNoNeedFormat
        ? curPageConfig.tableFormatter({
          list: tmpTableList,
          metricMap: curPageConfig?.tableMetricList,
          keyMap: curPageConfig?.keyMap(),
          entityType: filter.marketType,
          dimen: filter.dimension,
          granularity: filter.granularity,
          genreMap: currentGenreMap,
          ieggGenreMap,
        }) || []
        : tmpTableList;
      setTableData(result);
    } else {
      setTableData(tmpTableList);
      setTableCheck(tmpCheckList);
    }
    tableActionDispatch({ type: 'total', payload: total });
    loadingDispatch({ type: 'table', payload: false });
  };

  const backfillingAfterSearch = () => {
    if (cachedParams?.current?.select_code) {
      cachedParams.current.select_code = [];
    }
    const { pageIndex, pageSize } = tableAction;
    tableActionDispatch({
      type: 'pageIndex',
      payload: pageIndex,
    });
    renderTableData({
      tableList: originalTableData.current?.slice((pageIndex - 1) * pageSize, pageIndex * pageSize),
      checkList: tableAction.selectedList,
      cond: {
        total: originalTableData.current.length,
      },
    });
  };

  const getTrueFetchFn = (type: 'table' | 'line', dimension: string) => {
    switch (dimension) {
      case 'games':
        return {
          targetName: 'list',
          fn: type === 'line' ? getMobileStatsGameChart : getMobileStatsGameTable,
        };
      case 'companies':
        return {
          targetName: 'list',
          fn: type === 'line' ? getMobileStatsCompanyChart : getMobileStatsCompanyTable,
        };
      default:
        return {
          targetName: 'metric_value_list',
          fn: getMobilePageData,
        };
    }
  };

  const getTableDataFromInterface = ({
    modifiedParams,
    init = false,
    isOuter = false,
  }: {
    modifiedParams: IModifiedParams;
    init?: boolean;
    isOuter?: boolean;
  }): Promise<[]> => {
    loadingDispatch({ type: 'table', payload: true });
    return new Promise<[]>((resolve, reject) => {
      const requestParams = getParams({ type: 'table', modifiedParams });
      if (!isEmpty(requestParams)) {
        const { targetName, fn } = getTrueFetchFn('table', filter.dimension);
        fn(requestParams)
          .then((result: any) => {
            const resultData = result?.data || {};
            const { start_date: startDate, end_date: endDate } = result?.req || {};
            const tmpMarketType = modifiedParams?.entity_type;
            // 有异步问题，所以直接使用传入的值获取参数
            const tmpCurPageConfig = getCurPageConfig({
              marketType: tmpMarketType,
              dimension: modifiedParams?.data_tag,
            });
            let { result: formattedTableData } =
              tmpCurPageConfig.tableFormatter({
                list: resultData[targetName] || [],
                metricMap: tmpCurPageConfig?.tableMetricList,
                keyMap: tmpCurPageConfig?.keyMap(),
                entityType: tmpMarketType || filter.marketType,
                dimen: modifiedParams?.data_tag || filter.dimension,
                granularity: modifiedParams?.granularity || filter.granularity,
                genreMap: currentGenreMap,
                ieggGenreMap,
              }) || [];
            if (modifiedParams?.data_tag || filter.dimension === 'genres') {
              formattedTableData = getSortedTableData({
                list: JSON.parse(JSON.stringify(formattedTableData)),
                sortKey: `${filter?.dataSource}_revenue_0`,
                sortOrder: 'desc',
                sortPosition: 'other',
                matchKey: 'metric',
                valueType: 'other',
                pageConfig: curPageConfig,
              });
              formattedTableData = formattedTableData.map((item, index: number) => ({
                ...item,
                rank: index,
                colorRank: index - 1,
              }));
            }
            if (!isOuter) {
              originalTableData.current = formattedTableData;
            }
            const checkedList = formattedTableData?.slice(0, 6) || [];
            if (init) {
              tableActionDispatch({ type: 'selectedList', payload: checkedList });
              resolve({
                result: checkedList,
                // 从接口获取到的开始和结束时间在operation data外的榜单未返回
                startDate: startDate || modifiedParams?.start_date,
                endDate: endDate || modifiedParams?.end_date,
              });
            } else {
              resolve({
                result: []
              });
            }
            renderTableData({
              tableList: formattedTableData,
              checkList: checkedList,
              cond: {
                startDate,
                endDate,
                total: resultData.total,
              },
            });
          })
          .catch((e) => {
            console.error(e);
            renderTableData({
              tableList: [],
              checkList: [],
              cond: {},
            });
            loadingDispatch({ type: 'table', payload: false });
            reject([]);
          });
      } else {
        renderTableData({
          tableList: [],
          checkList: [],
          cond: {},
        });
        loadingDispatch({ type: 'table', payload: false });
        reject([]);
      }
    });
  };

  const getTableDataFront = ({ modifiedParams }: { modifiedParams: IModifiedParams; }): Promise<[]> => {
    loadingDispatch({ type: 'table', payload: true });
    return new Promise((resolve, reject) => {
      try {
        const { page: pageIndex = 1, page_size: pageSize = 10 } = modifiedParams;
        renderTableData({
          tableList: originalTableData.current.slice(
            (pageIndex - 1) * pageSize,
            pageIndex * pageSize,
          ),
          checkList: tableAction.selectedList,
          cond: {
            total: originalTableData.current.length,
          },
        });
        resolve({
          result: tableAction.selectedList,
        });
      } catch (error) {
        reject(error);
      }
    });
  };

  const getTableData = ({
    modifiedParams,
    init = false,
    isOuter = false,
    isFrontRender = false,
  }: {
    init?: boolean;
    isOuter?: boolean;
    isFrontRender?: boolean;
    modifiedParams: IModifiedParams;
  }): Promise<[]> => {
    if (isFrontRender)
      return getTableDataFront({
        modifiedParams,
      });
    return getTableDataFromInterface({
      modifiedParams,
      init,
      isOuter,
    });
  };

  const changeLineChartDataByLocale = (_locale: string) => {
    const tmpObj: any = {};
    Object.keys(originalLineData.current).forEach((key: string) => {
      tmpObj[key] = originalLineData.current?.[key].map((item) => ({
        ...item,
        name:
          filter.dimension === 'operation' && item?.nameKey
            ? formatMessage({
              id: item?.nameKey || '',
            })
            : _locale === 'zh-CN'
              ? item.nameCn
              : item.nameEn,
      }));
    });
    setLineChartData(tmpObj);
    loadingDispatch({ type: 'lineChart', payload: false });
  };

  const getLineChartData = ({ modifiedParams }: { modifiedParams: IModifiedParams; }) => {
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 创建新的控制器
    const controller = new AbortController();
    abortControllerRef.current = controller;

    loadingDispatch({ type: 'lineChart', payload: true });
    setLineChartData([]);
    const { targetName, fn } = getTrueFetchFn('line', filter.dimension);
    const requestParams = getParams({ type: 'line', modifiedParams });
    fn({
      ...requestParams,
      signal: controller.signal,
    })
      .then((result) => {
        const resultData = result?.data || {};
        const tmpCurPageConfig = getCurPageConfig({
          marketType: modifiedParams?.entity_type,
          dimension: modifiedParams?.data_tag,
        });
        const formattedLineChartData =
          tmpCurPageConfig.lineFormatter({
            list: resultData[targetName] || [],
            keyMap: tmpCurPageConfig?.keyMap(),
            metricMap: tmpCurPageConfig?.lineMetricList,
            isCn: locale === 'zh-CN',
            timeType: modifiedParams?.granularity || filter.granularity,
            genreMap: currentGenreMap,
            startDate: modifiedParams?.start_date || filter.startDate,
            endDate: modifiedParams?.end_date || filter.endDate,
          }) || [];
        originalLineData.current = formattedLineChartData;
        setLineChartData(formattedLineChartData);
        loadingDispatch({ type: 'lineChart', payload: false });
      })
      .catch((e) => {
        if (e.name !== 'AbortError') { // 忽略中止错误
          console.error(e);
          setLineChartData([]);
          loadingDispatch({ type: 'lineChart', payload: false });
        }
      });
  };
  // TODO: 非通用方案，key可能是从annie取，也可能从st取，后续考虑如何处理
  const getLineTotalText = ({ resultData, config }) =>
    config?.map((item) => ({
      ...item,
      value: item?.key?.includes('revenue') ? resultData.revenue : resultData.download,
    })) || [];

  // const getSortLineDataWhenFront = ({ lineData, modifiedParams }) => {
  //   Object.values(lineData).forEach((metricLineData) => {
  //       ``
  //   });
  // };

  const getDataWhenFront = ({ isRequestLineData, modifiedParams }: {
    isRequestLineData: boolean;
    modifiedParams: IModifiedParams;
  }) => {
    loadingDispatch({ type: 'table', payload: true });
    const { order, originalSortKey, page: pageIndex = 1 } = modifiedParams || {};
    const { pageSize } = tableAction;
    try {
      let sortedTable = getSortedTableData({
        list: JSON.parse(JSON.stringify(originalTableData.current)),
        sortKey: originalSortKey,
        sortOrder: order,
        sortPosition: originalSortKey?.match('date') ? 'main' : 'other',
        matchKey: originalSortKey?.match('date') ? 'date' : 'metric',
        valueType: originalSortKey?.match('date') ? 'date' : 'other',
        pageConfig: curPageConfig,
      }).map((item, index: number) => ({
        ...item,
        rank: index,
        colorRank: index - 1,
      }));
      originalTableData.current = sortedTable;
      sortedTable = sortedTable.slice((pageIndex - 1) * pageSize, pageIndex * pageSize);
      const startNum = (pageIndex - 1) * pageSize;
      renderTableData({
        tableList: sortedTable,
        checkList: sortedTable.slice(startNum, startNum + pageSize - 1),
        cond: {
          total: originalTableData.current?.length,
        },
      });
      const checkedList = sortedTable?.slice(0, 6) || [];
      tableActionDispatch({ type: 'selectedList', payload: checkedList });
      if (isRequestLineData) {
        loadingDispatch({ type: 'lineChart', payload: true });
        getLineChartData({ modifiedParams: { ...modifiedParams, checkedList } });
      } else {
        setLineChartData(originalLineData.current);
        loadingDispatch({ type: 'lineChart', payload: false });
      }
    } catch (error) {
      loadingDispatch({ type: 'table', payload: false });
      loadingDispatch({ type: 'lineChart', payload: false });
    }
  };

  /**
   * 获取表格和折线图数据的处理函数
   * @param {Object} params - 参数对象
   * @param {boolean} [params.isFrontSort] - 是否为前端排序
   * @param {any} [params.externalData] - 外部数据
   * @param {Object} [params.modifiedParams] - 修改后的参数
   *
   * 主要功能:
   * 1. 根据传入参数获取表格和折线图所需数据
   * 2. 处理外部数据和接口数据两种数据来源
   * 3. 对数据进行格式化并更新到对应的状态
   * 4. 控制加载状态
   *
   * @returns {void}
   */
  const getDataWhenOpera = ({
    isFrontSort,
    isRequestLineData = false,
    modifiedParams,
  }: {
    isFrontSort?: boolean;
    isRequestLineData?: boolean;
    modifiedParams: IModifiedParams;
  }) => {
    loadingDispatch({ type: 'table', payload: true });
    const tmpMarketType = modifiedParams?.entity_type;
    // 有异步问题，所以直接使用传入的值获取参数
    const tmpCurPageConfig = getCurPageConfig({
      marketType: tmpMarketType,
      dimension: modifiedParams?.data_tag,
    });
    const getTableAndLineData = ({ ori: originalData, sorted: sortedData }: any) => {
      const { result, count = {} } =
        curPageConfig.tableFormatter({
          list: sortedData || [],
          metricMap: tmpCurPageConfig?.tableMetricList,
          keyMap: tmpCurPageConfig?.keyMap(),
          entityType: tmpMarketType || filter.marketType,
          dimen: modifiedParams?.data_tag || filter.dimension,
          granularity: modifiedParams?.granularity || filter.granularity,
          genreMap: currentGenreMap,
          ieggGenreMap,
        }) || [];
      // 折线图不跟随排序，只根据原始数据的日期展示
      const formattedLineChartData =
        tmpCurPageConfig.lineFormatter({
          list: originalData || [],
          keyMap: tmpCurPageConfig?.keyMap(),
          metricMap: tmpCurPageConfig?.lineMetricList,
          localeFn: formatMessage,
          timeType: modifiedParams?.granularity || filter.granularity,
          genreMap: currentGenreMap,
          startDate: modifiedParams?.start_date || filter.startDate,
          endDate: modifiedParams?.end_date || filter.endDate,
        }) || [];
      originalLineData.current = formattedLineChartData;
      originalTableData.current = result;
      return {
        table: result || [],
        lineChart: formattedLineChartData || [],
        count,
      };
    };
    if (isFrontSort) {
      getDataWhenFront({ isRequestLineData, modifiedParams });
      return;
    }
    const { targetName, fn } = getTrueFetchFn('table', filter.dimension);
    fn(getParams({ type: 'table', modifiedParams }))
      .then((result: any) => {
        const resultData = result?.data || {};
        const { start_date: startDate, end_date: endDate } = result?.req || {};
        const sortedTableData = getSortedTableData({
          list: JSON.parse(JSON.stringify(resultData[targetName] || [])),
          sortKey: 'date_0',
          sortOrder: 'desc',
          sortPosition: 'main',
          matchKey: 'name',
          valueType: 'date',
          pageConfig: tmpCurPageConfig,
        });
        const { table, lineChart, count } = getTableAndLineData({
          ori: resultData[targetName],
          sorted: sortedTableData,
        });
        renderTableData({
          tableList: table,
          checkList: table,
          cond: {
            startDate,
            endDate,
            total: table?.length,
          },
        });
        setLineChartSumData(
          getLineTotalText({ resultData: count, config: tmpCurPageConfig?.lineTotalText }),
        );
        setLineChartData(lineChart);
        loadingDispatch({ type: 'lineChart', payload: false });
      })
      .catch((e) => {
        console.error(e);
        renderTableData({
          tableList: [],
          checkList: [],
          cond: {},
        });
        setLineChartSumData([]);
        loadingDispatch({ type: 'table', payload: false });
        loadingDispatch({ type: 'lineChart', payload: false });
      });
  };

  const reset = () => {
    setTableData([]);
    setLineChartData([]);
    setLineChartSumData([]);
  };

  /**
   * 处理默认排序键值的设置
   * @param {object} params - 参数对象
   * @param {IModifiedParams} [params.modifiedParams] - 修改后的统计参数
   * @returns {object} 返回排序配置对象
   *   - order: 排序方向('desc')
   *   - order_metric: 排序指标
   * @description
   * - 根据维度(data_tag)设置默认排序字段
   * - 当维度为'operation'时使用'date'作为排序键
   * - 其他维度使用当前页面配置的第一个指标作为排序键
   * - 同时更新表格状态中的sortKey和sortKeyLabel
   */
  const handleDefaultSortKey = ({ modifiedParams }: { modifiedParams: IModifiedParams; }) => {
    const dimens = modifiedParams?.data_tag || filter.dimension;
    const order = modifiedParams.order || 'desc';
    let orderBy = modifiedParams.order_metric || 'date';
    let orderByLabel = modifiedParams.originalSortKey || `${orderBy}_0`;
    if (dimens === 'operation') {
      tableActionDispatch({ type: 'sortKey', payload: orderBy });
      tableActionDispatch({ type: 'sortKeyLabel', payload: orderByLabel });
    } else {
      const tmpCurPageConfig = getCurPageConfig({
        marketType: modifiedParams?.entity_type,
        dimension: modifiedParams?.data_tag,
      });
      const find =
        tmpCurPageConfig?.tableMetricList.find(
          (item) => item.metric === `${filter?.dataSource}_revenue`,
        ) || {};
      orderBy = find?.metric;
      orderByLabel = `${orderBy}_0`;
      tableActionDispatch({ type: 'sortKey', payload: orderBy });
      tableActionDispatch({ type: 'sortKeyLabel', payload: orderByLabel });
    }
    Object.assign(modifiedParams, {
      order,
      order_metric: orderBy,
    });
  };

  /**
   * 处理特殊参数的逻辑函数
   * @param {Object} params - 参数对象
   * @param {boolean} params.pageInit - 页面是否初始化
   * @param {Object} params.modifiedParams - 需要修改的参数对象
   *
   * 主要功能:
   * 1. 当页面初始化且维度为companies时,设置默认的时间粒度为monthly
   * 2. 处理默认排序参数
   * 3. 更新filter状态
   */
  const handleSpecialParmas = ({ pageInit, modifiedParams }: {
    pageInit: boolean;
    modifiedParams: IModifiedParams;
  }) => {
    if (pageInit && filter?.dimension === 'companies' && !modifiedParams?.granularity && filter?.granularity !== 'monthly') {
      handleDateFilterWhenCompaniesAndSensorTower({ modifiedParams });
    }
    if (pageInit) {
      handleDefaultSortKey({ modifiedParams });
    }
  };

  /**
   * 初始化数据加载函数
   * @param {Object} params - 初始化参数对象
   * @param {boolean} [params.isFrontSort] - 是否为前端排序
   * @param {any} [params.isRequestLineData] - 折线图数据是否跟随变化
   * @param {Object} [params.modifiedParams] - 修改后的参数
   * @param {boolean} [params.init=false] - 是否为初始化
   *
   * @description
   * 该函数用于初始化数据加载,主要功能包括:
   * - 重置数据状态
   * - 处理排序参数
   * - 根据维度类型(operation/其他)分别获取数据
   * - 处理时间范围参数
   * - 获取表格数据和折线图数据
   */
  const initial = async ({
    init = false,
    // 页面初始化自发的请求
    pageInit = false,
    isFrontSort = false,
    isRequestLineData = false,
    modifiedParams = {},
  }: {
    isFrontSort?: boolean;
    isRequestLineData?: boolean;
    init?: boolean;
    pageInit?: boolean;
    modifiedParams: IModifiedParams;
  }) => {
    reset();
    handleSpecialParmas({ pageInit, modifiedParams });
    loadingDispatch({ type: 'lineChart', payload: true });
    const dimens = modifiedParams?.data_tag || filter.dimension;
    if (dimens === 'operation') {
      getDataWhenOpera({ isFrontSort, isRequestLineData, modifiedParams });
    } else if (dimens === 'genres' && isFrontSort) {
      getDataWhenFront({ isRequestLineData, modifiedParams });
    } else {
      try {
        const { result, startDate, endDate } = await getTableData({ modifiedParams, init });
        if (result?.length) {
          getLineChartData({ modifiedParams: { ...modifiedParams, checkedList: result, start_date: startDate, end_date: endDate } });
        } else {
          loadingDispatch({ type: 'lineChart', payload: false });
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  // 根据hideCond判断字段是否展示
  const getMetricShow = (metricItem: any) => {
    const { hideCond } = metricItem;
    if (!hideCond) return true;
    if (
      hideCond.includes(filter.dataSource) ||
      hideCond.includes(GRANULARITY_MAP[filter.granularity])
    )
      return false;
    return true;
  };

  const getMetricSeg = (_metricList: any[]) => _metricList.filter((item) => getMetricShow(item));

  /**
   * 根据类型获取表格唯一键名, 该键名用于表格checkbox中颜色和勾选
   * @param type - 表格类型,可选值:'genres'|'operation'|其他
   * @returns 返回对应的键名:'genre'|'date'|'name'
   */
  const getTableUniqueKey = (type: string) => {
    if (type === 'genres') return 'genre';
    if (type === 'operation') return 'date';
    return 'name';
  };

  const getHtmlContent = () => {
    const getTable = () => {
      if (filter.dimension === 'operation' && loading.table) {
        // operation页loading只展示一个
        return <></>;
      }
      return (
        <DynamicTable
          isDetail={true}
          tableData={tableData}
          isLoading={loading.table}
          entityType={filter.marketType}
          tableSelected={tableAction.selectedList}
          tableSelectedAll={tableAction.SelectionAll}
          total={tableAction.total}
          isNotPageableAuto={filter.dimension !== 'operation'}
          sortKey={tableAction.sortKeyLabel}
          sortOrder={tableAction.sortOrder}
          pageSize={tableAction.pageSize}
          pageIndex={tableAction.pageIndex}
          scrollMinWidth={tableScrollWidth}
          keyMap={curPageConfig.keyMap()}
          getDateColFormat={handleDateColFormat}
          metricCol={getMetricSeg(curPageConfig.tableMetricList)}
          firstCol={curPageConfig.tableFirstCol}
          specialCol={curPageConfig.tableSpecialCol}
          getIsHideCol={getIsHideCol}
          ratioHideCol={curPageConfig.ratioHideCol}
          pageSizeOptions={[10, 20, 50]}
          onPagingChange={handlePagingChange}
          onSortChange={handleSortChange}
          onTableCheckboxChange={handleTableCheckboxChange}
          onGameItemClick={handleGameItemClick}
          uniqueKey={getTableUniqueKey(filter.dimension)}
        />
      );
    };
    if (filter.dimension === 'operation')
      return (
        <CardSlotV2
          // title={formatMessage({ id: curPageConfig?.title })}
          className="dbi-stats-data"
          iconList={[]}
        >
          {/* {getChart()} */}
          {getTable()}
        </CardSlotV2>
      );
    return (
      <>
        <CardSlotV2 iconList={[]}>{getTable()}</CardSlotV2>
      </>
    );
  };

  // 使用统一全局存储数据
  const { condDatas, filterLoading } = useSetQueryDefinition({ ...COND_PARAMS.mobileMainGenre });

  // 获取主品类筛选器选项
  const genreOptions = useMemo(() => {
    if (filterLoading) return [];
    const genreListTemp = condDatas?.mobileMainGenre || [];
    return genreListTemp?.map((itemValue: string) => {
      const iegGenreList = FULL_GENRE?.ieg || [];
      const itemGenreObj = iegGenreList?.find((item: any) => item?.main_genre?.genre == itemValue);
      let textValue = itemValue;
      if (itemGenreObj) {
        textValue =
          locale === LANGUAGE_CN
            ? itemGenreObj?.main_genre?.genre_cn
            : itemGenreObj?.main_genre?.genre_en;
      }
      return {
        value: itemValue,
        text: textValue,
      };
    });
  }, [condDatas, filterLoading, locale]);

  useEffect(() => {
    const pageKey = `${filter.marketType}_${filter.dimension}`;
    const pageLogConfigKey = PAGE_LOG_CONFIG_KEYS[pageKey];
    const pageLogConfig = dbiPageLogMes[pageLogConfigKey];
    if (!pageLogConfig) return;
    buriedPointPage(pageLogConfig);
    setPageLogInfo(pageLogConfig);
  }, [filter.marketType, filter.dimension]);

  useEffect(() => {
    changeLineChartDataByLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (!isGenreLoading) {
      initial({ init: true, pageInit: true, modifiedParams: {} });
    } else {
      loadingDispatch({ type: 'table', payload: true });
      loadingDispatch({ type: 'lineChart', payload: true });
    }
  }, [isGenreLoading]);

  useEffect(() => {
    // 修复在升序情况下，切换标签时依旧是升序
    tableActionDispatch({ type: 'sortOrder', payload: 'desc' });
  }, [filter.marketType]);

  // 组件卸载时清理
  useEffect(() => () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // 下载需要的接口参数
  const getDownloadReqParams = useCallback(() => {
    const paramsTemp = {
      filter,
      entityType,
      tableAction,
      market: marketNameEn,
      start_date: filter.startDate || '',
      end_date: filter.endDate || '',
      granularity: filter.granularity,
      mainGenreList: filter.singleGenre,
      iegg_genre_req: ieggGenreReq || [],
      dataType: filter.dimension,
    };
    return paramsTemp;
  }, [filter, entityType, tableAction, marketNameEn, ieggGenreReq]);

  return (
    <div>
      <Filters
        filterValues={filter}
        metricsList={metricsList}
        order={tableAction.sortOrder}
        orderBy={tableAction.sortKey}
        total={tableAction.total}
        onFilterChange={handleFilterChange}
        entityType={leftSegment}
        genreOptions={genreOptions}
        tableDownloadParams={getDownloadReqParams()}
      />
      <CardSlotV2
        title={formatMessage({ id: curPageConfig?.title })}
        className="dbi-stats-data"
        iconList={[]}
      >
        <StatsLineChart
          isLoading={loading.lineChart}
          metricSegments={getMetricSeg(curPageConfig?.lineMetricList)}
          metricList={lineChartSumData}
          metricInfo={curPageConfig?.lineMetricInfo}
          entityType={filter.marketType}
          lineChartData={lineChartData}
          dateType={filter.granularity || 'monthly'}
        />
      </CardSlotV2>
      {filter?.dimension !== 'operation' && (
        <GenreProfileSearch
          pageConfig={curPageConfig}
          marketType={filter.marketType}
          dimension={filter.dimension}
          genreName={marketNameEn}
          getPageParams={getParamsForSearch}
          searchApi={getTrueFetchFn('table', filter.dimension).fn}
          pageLogInfo={pageLogInfo}
          renderTableData={renderTableData}
          getTableData={getTableData}
          resetOuterTableData={backfillingAfterSearch}
          loadingDispatch={loadingDispatch}
          tableActionDispatch={tableActionDispatch}
          getSearchListFront={handleSearchListFront}
        />
      )}
      {getHtmlContent()}
    </div>
  );
}
